<template>
    <div v-if="editing">
        <b-form-group :disabled="disabled">
            <div>
                <template v-if="expand">
                    <label>Reference Name</label>
                    <HelpPopup>
                        Only used internally to reference specific questions and is not displayed to the user.
                    </HelpPopup>
                </template>

                <b-button v-if="!disabled" size="sm" variant="danger" class="pull-right mb-0"
                    style="margin-top: -10px; margin-right: -10px;" @click="$emit('remove')">
                    <i class="fa fa-minus" />
                    Remove Question
                </b-button>
            </div>
            <b-input v-if="expand" :value="question.name" @input="$emit('update-question', { ...question, name: $event })" maxlength="200" />
            <template v-else>
                <strong>{{ displayName(question.position, question.name) }}</strong>

                <VueShowdown :markdown="question.text" :options="showdownOptions" />
            </template>
        </b-form-group>

        <Transition name="slide">
            <div v-if="expand">
                <div class="d-flex mb-2">
                    <div class="flex-fill">
                        <EnumSelector :value="question.typeId" @input="handleTypeChange" :enum-codes="UserTaskQuestionType" label="Type"
                            :disabled="disabled" class="mb-0" :elements-to-discard="disallowedQuestionTypes" />
                        <div v-if="question.typeId === UserTaskQuestionType.textCopy" class="text-muted">
                            <small>
                                <i class="fas fa-asterisk"></i>
                                Only the question text will appear for this type. The user will not be prompted to
                                provide an answer.
                            </small>
                        </div>
                    </div>

                    <b-form-group class="text-center">
                        <label>Required</label>
                        <HelpPopup>
                            If selected, the question must be answered. If the question has
                            multiple Next Question branches defined, it is automatically required.
                        </HelpPopup>
                        <b-check v-if="question.typeId === UserTaskQuestionType.textCopy" switch disabled />
                        <b-check v-else-if="hasMultipleBranches" switch :checked="true" disabled />
                        <b-check v-else switch :checked="question.required" @change="$emit('update-question', { ...question, required: $event })" :disabled="disabled" />
                    </b-form-group>
                </div>

                <b-form-group>
                    <label><strong>Text*</strong></label>
                    <HelpPopup
                        markdown="The question text to display to the user. [Markdown is supported](https://www.markdownguide.org/getting-started/)."
                        :markdown-options="showdownOptions" />
                    <b-textarea rows="2" :value="question.text" @input="$emit('update-question', { ...question, text: $event })" :disabled="disabled" :autofocus="true" />
                </b-form-group>

                <b-form-group>
                    <label>Help Text</label>
                    <HelpPopup
                        markdown="If provided, a help icon will appear and this text will appear when it is selected. [Markdown is supported](https://www.markdownguide.org/getting-started/)."
                        :markdown-options="showdownOptions" />
                    <b-input :value="question.helpText" @input="$emit('update-question', { ...question, helpText: $event })" :disabled="disabled" />
                </b-form-group>

                <b-form-group v-if="question.typeId !== UserTaskQuestionType.multipleChoiceSingle"
                    label="Next Question">
                    <b-select size="sm" :value="question.nextQuestionPosition" @input="$emit('update-question', { ...question, nextQuestionPosition: $event })" :options="branchOptions"
                        :disabled="disabled" />
                </b-form-group>

                <b-table-simple
                    v-if="[UserTaskQuestionType.multipleChoiceSingle, UserTaskQuestionType.multipleChoiceMultiple].includes(question.typeId)"
                    small>
                    <thead>
                        <tr>
                            <th v-if="!disabled" class="handle"></th>
                            <th>Choices</th>
                            <th v-if="question.typeId === UserTaskQuestionType.multipleChoiceSingle" class="branch">
                                Next
                                Question
                            </th>
                            <th class="remove"></th>
                        </tr>
                    </thead>
                    <Draggable v-model="question.options" tag="tbody" handle=".handle">
                        <tr v-for="(o, i) in question.options" :key="o.id">
                            <td v-if="!disabled" class="handle">
                                <i class="fas fa-grip-horizontal" />
                            </td>
                            <td>
                                <b-input size="sm" :value="o.text" @input="updateOption(i, 'text', $event)" :disabled="disabled" :autofocus="!!i" />
                            </td>
                            <td v-if="question.typeId === UserTaskQuestionType.multipleChoiceSingle">
                                <b-select size="sm" :value="o.nextQuestionPosition" @input="updateOption(i, 'nextQuestionPosition', $event)" :options="branchOptions"
                                    :disabled="disabled" />
                            </td>
                            <td>
                                <b-button variant="danger" size="sm" title="Remove Choice" @click="removeChoice(i)"
                                    :disabled="disabled || question.options.length <= 1">
                                    <i class="fa fa-minus" />
                                </b-button>
                            </td>
                        </tr>
                    </Draggable>
                    <tfoot>
                        <tr>
                            <th :colspan="question.typeId === UserTaskQuestionType.multipleChoiceSingle ? 4 : 3">
                                <b-button v-if="!disabled" variant="primary" size="sm" @click="addChoice">
                                    Add Choice
                                </b-button>
                            </th>
                        </tr>
                    </tfoot>
                </b-table-simple>

                <div v-if="question.typeId === UserTaskQuestionType.acceptanceConfigurationsRequired">
                    <AcceptanceConfigurationSelector
                        :value="question.acceptanceConfigurations || []"
                        @input="updateAcceptanceConfigurations"
                        :disabled="disabled"
                        label="Acceptance Configurations"
                    />
                    <div class="text-muted">
                        <small>
                            <i class="fas fa-asterisk"></i>
                            The patient will be required to accept these configurations when completing the task.
                            If any configuration requires a signature, the patient must provide a digital signature.
                        </small>
                    </div>
                </div>
            </div>
        </Transition>

        <b-alert :show="!!validationErrorsToDisplay" variant="danger" class="p-2 font-weight-bold">
            <i class="fa fa-warning" />
            {{ validationErrorsToDisplay }}
        </b-alert>

        <div class="text-center">
            <b-button size="sm" class="m-0" @click="expand = !expand">
                {{ expand ? 'Collapse' : (disabled ? 'Details' : 'Edit') }}
                <i :class="{ 'fas fa-chevron-down': true, 'fa-rotate-180': expand }" style="transition: all .3s;"></i>
            </b-button>
        </div>
    </div>
    <div v-else>
        <div class="mb-2">
            <div class="pull-right">
                <HelpPopup v-if="question.helpText" :markdown="question.helpText" :markdown-options="showdownOptions" />
                <b-badge v-if="isRequired" variant="info">
                    Required
                </b-badge>
            </div>

            <div :class="{ 'mb-0': true }">
                <VueShowdown :markdown="question.text" :options="showdownOptions" />
            </div>
        </div>
        <template v-if="question.typeId == UserTaskQuestionType.multipleChoiceSingle">
            <b-form-radio-group :options="optionsToSelect" stacked :disabled="disabled" :checked="answerValue"
                @change="applyAnswer($event)" />
        </template>
        <template v-if="question.typeId == UserTaskQuestionType.multipleChoiceMultiple">
            <b-form-checkbox-group :options="optionsToSelect" stacked :disabled="disabled" :checked="answerValue"
                @change="applyAnswer($event.join(','))" />
            <p class="text-muted"><small><em>Select all that apply</em></small></p>
        </template>
        <b-input v-if="question.typeId == UserTaskQuestionType.freeTextShort" :disabled="disabled" :value="answerValue"
            @input="applyAnswer($event)" />
        <b-textarea v-if="question.typeId == UserTaskQuestionType.freeTextLong" rows="2" :disabled="disabled"
            :value="answerValue" @input="applyAnswer($event)" />
        <b-input type="number" v-if="question.typeId == UserTaskQuestionType.numeric" :disabled="disabled"
            :value="answerValue" @input="applyAnswer($event)" />
        <el-date-picker v-if="question.typeId == UserTaskQuestionType.date" :disabled="disabled" :value="answerValue"
            @input="applyAnswer($event)" format="MM/dd/yyyy" />
        <template v-if="question.typeId == UserTaskQuestionType.acceptanceConfigurationsRequired">
            <ul class="list-unstyled mb-0">
                <li v-for="config in question.acceptanceConfigurations" :key="config.id">
                    <router-link :to="{ name: 'AcceptanceConfigurationDetails', params: { id: config.id } }" target="_blank">
                        {{ config.name }}
                        <i class="fas fa-external-link-alt fa-sm ml-1"></i>
                    </router-link>
                </li>
            </ul>
            <p v-if="!question.acceptanceConfigurations?.length" class="text-muted mb-0">
                <em>No acceptance configurations specified</em>
            </p>
        </template>

        <b-alert :show="!!validationErrorsToDisplay" variant="danger" class="p-2 mt-1 font-weight-bold">
            <i class="fa fa-warning" />
            {{ validationErrorsToDisplay }}
        </b-alert>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { UserTaskQuestion, UserTaskQuestionType, UserTaskAnswer } from '@/models/UserTask';
import { AcceptanceConfiguration } from '@/models/AcceptanceConfiguration';
import { EntityType } from '@/models/EntityType';
import EnumSelector from '../EnumSelector.vue';
import Draggable from 'vuedraggable';
import HelpPopup from '../HelpPopup.vue';
import { DatePicker } from "element-ui";
import AcceptanceConfigurationSelector from './AcceptanceConfigurationSelector.vue';

@Component({
    components: {
        EnumSelector,
        Draggable,
        HelpPopup,
        [DatePicker.name]: DatePicker,
        AcceptanceConfigurationSelector,
    }
})
export default class TaskQuestion extends Vue {
    @Prop({ required: true }) question!: UserTaskQuestion;
    @Prop({ required: true }) questions!: UserTaskQuestion[];
    @Prop({ default: [] }) answers!: UserTaskAnswer[];
    @Prop({ default: false }) disabled!: boolean;
    @Prop({ default: false }) editing!: boolean;
    @Prop({ default: false }) showErrors!: boolean;
    @Prop({ default: false }) collapseAll!: boolean;
    @Prop({ default: EntityType.UserTask }) entityType!: EntityType;

    @Watch("questions")
    questionsUpdated() {
        this.$nextTick(() => {
            if (!this.branchOptions.some(bo => bo.value == this.question.nextQuestionPosition)) {
                this.question.nextQuestionPosition = this.nextBranchOption.value;
            }

            this.question.options.forEach((qo: any) => {
                if (!this.branchOptions.some(bo => bo.value == qo.nextQuestionPosition)) {
                    qo.nextQuestionPosition = this.nextBranchOption.value;
                }
            });
        });
    }

    @Watch("collapseAll")
    collapseAllUpdated() {
        this.expand = !this.collapseAll;
    }

    private expand = !this.disabled;

    UserTaskQuestionType = UserTaskQuestionType;

    get disallowedQuestionTypes(): UserTaskQuestionType[] {
        if (this.entityType !== EntityType.UserTask) {
            return [UserTaskQuestionType.acceptanceConfigurationsRequired];
        }
        return [];
    }

    get branchOptions() {
        const futureQuestions = this.questions
            .filter(q => q.position > this.question.position)
            .map(q => ({
                value: q.position,
                text: `${q.position} - ${this.displayName(q.position, q.name)}`
            }));

        const end = {
            value: -1,
            text: "E - End Questions"
        };

        return [...futureQuestions, end];
    }

    get nextBranchOption() {
        const [firstOption] = this.branchOptions;
        return firstOption;
    }

    get showdownOptions() {
        return { openLinksInNewWindow: true, simplifiedAutoLink: true };
    }

    addChoice() {
        const updatedOptions = [...this.question.options, {
            text: "",
            nextQuestionPosition: this.nextBranchOption?.value
        }];
        this.$emit('update-question', {
            ...this.question,
            options: updatedOptions
        });
    }

    updateOption(optionIndex: number, prop: string, value: any) {
        const updatedOptions = [...this.question.options];
        updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            [prop]: value
        };
        this.$emit('update-question', {
            ...this.question,
            options: updatedOptions
        });
    }

    removeChoice(index: number) {
        const updatedOptions = [...this.question.options];
        updatedOptions.splice(index, 1);
        this.$emit('update-question', {
            ...this.question,
            options: updatedOptions
        });
    }

    updateAcceptanceConfigurations(configs: AcceptanceConfiguration[]) {
        this.$emit('update-question', {
            ...this.question,
            acceptanceConfigurations: configs
        });
    }

    displayName(position: number, name?: string) {
        return name ? name : "Question #" + position;
    }

    handleTypeChange(newTypeId: UserTaskQuestionType) {
        const updatedQuestion = { ...this.question, typeId: newTypeId };

        // If changing to multiple choice type and no options exist or all options are empty, add a default empty option
        if ([UserTaskQuestionType.multipleChoiceSingle, UserTaskQuestionType.multipleChoiceMultiple].includes(newTypeId)) {
            if (!updatedQuestion.options || updatedQuestion.options.length === 0 || updatedQuestion.options.every(o => !o.text?.trim())) {
                updatedQuestion.options = [{
                    text: "",
                    nextQuestionPosition: this.nextBranchOption?.value || -1
                }];
            }
        }

        this.$emit('update-question', updatedQuestion);
    }

    get optionsToSelect() {
        return this.question.options.map(o => ({ value: o.text, text: o.text }));
    }

    get answerValue() {
        const answer = this.answers?.find(a => a.taskQuestionId == this.question.id)?.answer;
        if (this.question.typeId === UserTaskQuestionType.multipleChoiceMultiple) {
            return answer?.split(",").map(a => a.trim()).filter(a => a);
        }
        return answer;
    }

    applyAnswer(value: string) {
        const answer = this.answers?.find(a => a.taskQuestionId == this.question.id);

        if (answer) {
            answer.answer = value;
            this.$emit("input");
            return;
        }

        this.answers.push({
            taskQuestionId: this.question.id,
            answer: value,
        });

        this.$emit("input");
    }

    get hasMultipleBranches() {
        return !this.question.options.every(o => o.nextQuestionPosition === this.question.options[0].nextQuestionPosition);
    }

    get isRequired() {
        if (this.question.typeId === UserTaskQuestionType.textCopy) return false;
        if (this.question.required) return true;
        if (this.question.typeId !== UserTaskQuestionType.multipleChoiceSingle) return false;
        return this.hasMultipleBranches;
    }

    get validationErrorsToDisplay() {
        if (!this.showErrors) return undefined;

        if (this.editing) {
            if (!this.question.text?.trim()) return "Question Text is required.";
            if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(this.question.typeId)) return undefined;
            if (this.question.options.some(o => !o.text?.trim())) return "All Question Choices must have text.";
            const uniqueTexts = new Set(this.question.options.map(o => o.text?.trim()));
            if (uniqueTexts.size !== this.question.options.length) return "All Question Choices must be unique.";
            return undefined;
        }

        const answer = this.answers?.find(a => a.taskQuestionId == this.question.id)?.answer;

        if (this.isRequired && !answer) return "This question is required.";

        return undefined;
    }
}

</script>

<style scoped>
.table .handle {
    width: 10px;
}

.table .branch {
    width: 250px;
}

.table .remove {
    width: 30px;
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s;
    overflow: hidden;
    max-height: 100vh;
}

.slide-enter,
.slide-leave-to {
    max-height: 0;
    opacity: 0;
}
</style>
